import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './Calendar.css';

interface CalendarProps {
	selectedDate: string;
	onDateSelect: (date: string) => void;
	region?: string;
}

interface BookedDate {
	date: string;
	count: number;
}

interface BlockedDate {
	date: string;
	reason?: string;
	region?: string;
}

const Calendar: React.FC<CalendarProps> = ({ selectedDate, onDateSelect, region }) => {
	const { t, i18n } = useTranslation();
	const [currentMonth, setCurrentMonth] = useState(new Date());
	const [bookedDates, setBookedDates] = useState<BookedDate[]>([]);
	const [blockedDates, setBlockedDates] = useState<BlockedDate[]>([]);

	// 获取日历数据
	useEffect(() => {
		const fetchCalendarData = async () => {
			try {
				const response = await fetch(`/api/calendar${region ? `?region=${region}` : ''}`);
				const result = await response.json();
				if (result.success) {
					setBookedDates(result.bookedDates || []);
					setBlockedDates(result.blockedDates || []);
				}
			} catch (error) {
				console.error('Failed to fetch calendar data:', error);
			}
		};

		fetchCalendarData();
	}, [region, currentMonth]);

	// 获取月份名称
	const getMonthName = (date: Date): string => {
		return date.toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' });
	};

	// 获取星期几名称
	const getWeekDays = (): string[] => {
		const days = [];
		const baseDate = new Date(2024, 0, 7); // Sunday
		for (let i = 0; i < 7; i++) {
			const d = new Date(baseDate);
			d.setDate(baseDate.getDate() + i);
			days.push(d.toLocaleDateString(i18n.language, { weekday: 'short' }));
		}
		return days;
	};

	// 生成月份天数
	const getDaysInMonth = (): (number | null)[] => {
		const year = currentMonth.getFullYear();
		const month = currentMonth.getMonth();

		const firstDay = new Date(year, month, 1).getDay();
		const daysInMonth = new Date(year, month + 1, 0).getDate();

		const days: (number | null)[] = [];

		// 填充月初空白
		for (let i = 0; i < firstDay; i++) {
			days.push(null);
		}

		// 填充日期
		for (let i = 1; i <= daysInMonth; i++) {
			days.push(i);
		}

		return days;
	};

	// 格式化日期为 YYYY-MM-DD
	const formatDate = (day: number): string => {
		const year = currentMonth.getFullYear();
		const month = String(currentMonth.getMonth() + 1).padStart(2, '0');
		const d = String(day).padStart(2, '0');
		return `${year}-${month}-${d}`;
	};

	// 检查日期是否已过
	const isPastDate = (day: number): boolean => {
		const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		return date < today;
	};

	// 检查日期是否被关闭
	const isDateBlocked = (day: number): boolean => {
		const dateStr = formatDate(day);
		return blockedDates.some((b) => b.date === dateStr);
	};

	// 检查日期预约情况
	const getBookingStatus = (day: number): 'available' | 'limited' | 'busy' | 'blocked' => {
		const dateStr = formatDate(day);
		
		// 先检查是否被关闭
		if (blockedDates.some((b) => b.date === dateStr)) {
			return 'blocked';
		}

		const booked = bookedDates.find((b) => b.date === dateStr);

		if (!booked || booked.count === 0) return 'available';
		if (booked.count >= 3) return 'busy';
		return 'limited';
	};

	// 切换月份
	const changeMonth = (delta: number) => {
		const newMonth = new Date(currentMonth);
		newMonth.setMonth(newMonth.getMonth() + delta);

		// 不能选择过去的月份
		const today = new Date();
		if (newMonth.getFullYear() < today.getFullYear() ||
			(newMonth.getFullYear() === today.getFullYear() && newMonth.getMonth() < today.getMonth())) {
			return;
		}

		setCurrentMonth(newMonth);
	};

	// 处理日期点击
	const handleDateClick = (day: number) => {
		if (isPastDate(day)) return;
		if (isDateBlocked(day)) return;

		const dateStr = formatDate(day);
		onDateSelect(dateStr);
	};

	const weekDays = getWeekDays();
	const days = getDaysInMonth();

	return (
		<div className="calendar">
			<div className="calendar-header">
				<button
					type="button"
					className="month-btn"
					onClick={() => changeMonth(-1)}
					aria-label="Previous month"
				>
					◀
				</button>
				<span className="month-name">{getMonthName(currentMonth)}</span>
				<button
					type="button"
					className="month-btn"
					onClick={() => changeMonth(1)}
					aria-label="Next month"
				>
					▶
				</button>
			</div>

			<div className="calendar-weekdays">
				{weekDays.map((day) => (
					<div key={day} className="weekday">
						{day}
					</div>
				))}
			</div>

			<div className="calendar-days">
				{days.map((day, index) => {
					if (day === null) {
						return <div key={`empty-${index}`} className="day empty" />;
					}

					const dateStr = formatDate(day);
					const isPast = isPastDate(day);
					const status = getBookingStatus(day);
					const isSelected = dateStr === selectedDate;
					const isBlocked = status === 'blocked';

					return (
						<button
							key={day}
							type="button"
							className={`day ${isPast ? 'past' : ''} ${status} ${isSelected ? 'selected' : ''}`}
							onClick={() => handleDateClick(day)}
							disabled={isPast || isBlocked}
							aria-label={`${day}, ${isBlocked ? 'unavailable' : status === 'busy' ? 'busy' : status === 'limited' ? 'limited availability' : 'available'}`}
						>
							<span className="day-number">{day}</span>
							{!isPast && !isBlocked && (
								<span className={`status-dot ${status}`} />
							)}
							{isBlocked && (
								<span className="blocked-indicator">✕</span>
							)}
						</button>
					);
				})}
			</div>

			<div className="calendar-legend">
				<div className="legend-item">
					<span className="status-dot available" />
					<span>{t('calendar.available', 'Available')}</span>
				</div>
				<div className="legend-item">
					<span className="status-dot limited" />
					<span>{t('calendar.limited', 'Limited')}</span>
				</div>
				<div className="legend-item">
					<span className="status-dot busy" />
					<span>{t('calendar.busy', 'Busy')}</span>
				</div>
				<div className="legend-item">
					<span className="blocked-x">✕</span>
					<span>{t('calendar.unavailable', 'Unavailable')}</span>
				</div>
			</div>
		</div>
	);
};

export default Calendar;
